<template>
    <slot />
</template>

<script setup>
    import { useBloom } from '#imports';
    import gsap from 'gsap';
    import { CylinderGeometry, Mesh } from 'three';
    import { inject, ref, watch, watchEffect } from 'vue';

    import { toRadians } from '@resn/gozer-math';
    import { simpleMaterial } from '@resn/gozer-three';
    import { basePaneOptions, usePane, useRafBool, useThreeObject } from '@resn/gozer-vue';

    import { createTunnelMaterial } from './tunnel-material';
    import { GRAIL_TUNNEL_REVEAL, GRAIL_TUNNEL_SET, GRAIL_TUNNEL_WIPE } from '~/core/constants';
    import { useGlobalAssets } from '~/providers/GlobalAssets';

    const active = ref(false);

    const { object } = useThreeObject(null, { name: 'GrailTunnel', props: { v: active } });
    // const bloom = useBloom();
    // watch(
    //     () => bloom.pass.value,
    //     (pass) => {
    //         pass.selection?.add(tunnel);
    //     }
    // );

    // Provide defaults in case component is used outside of Card context (e.g., in sandbox)
    const cardContext = inject('card', null);
    const slabColor1 = cardContext?.slabColor1 ?? ref('#87b2ff');
    const slabColor2 = cardContext?.slabColor2 ?? ref('#bd8ef6');

    const radiusTop = ref(2);
    const radiusBottom = ref(2);
    // const height = ref(8);
    const height = ref(20);
    const segments = ref(12);
    const heightSegments = ref(12);
    const openEnded = ref(true);
    const rx = ref(90);

    const uInProgress = ref(0);
    const uNoiseScale1 = ref(4);
    const uNoiseScale2 = ref(1);
    const uNoiseScale3 = ref(0.2);
    const uNoiseSpeed = ref(0.05);
    const uNoiseSpeed3 = ref(0.3);
    const totalSpeedStr = ref(1);
    const uColorBrightness = ref(2.0);

    // Bend controls: angle (deg), radius, direction angle (deg around XZ)
    const bendAngleDeg = ref(20);
    // radius now derived; we provide height instead
    const bendDirDeg = ref(-35); // 0 -> +X, 90 -> +Z

    const color1 = ref('#08061f');
    const color2 = ref('#7c49d5');
    const color3 = ref('#f2e6cb');

    const materialBasic = simpleMaterial('red');
    // const material = new MeshNormalMaterial({ side: BackSide });
    const material = createTunnelMaterial();

    let geo = new CylinderGeometry();
    // let geo = new ConeGeometry();
    const tunnel = new Mesh(geo, material);
    object.add(tunnel);
    tunnel.renderOrder = -1;

    const onLoaded = ({ data }) => {
        const { noise1 } = data;
        material.uNoiseMap = noise1;
    };

    const show = ({ fast = false } = {}) => {
        active.value = true;

        const tl = new gsap.timeline({
            onStart: () => emit('start'),
        });

        // tl.fromTo(
        //     bendAngleDeg,
        //     { value: 0 },
        //     { value: 30, duration: 3, ease: 'sine.inOut' },
        //     startDelay
        // );
        // tl.fromTo(
        //     bendDirDeg,
        //     { value: -180 },
        //     { value: 0, duration: 3, ease: 'power2.inOut' },
        //     startDelay
        // );

        // tl.to(bendDirDeg, { value: 180, duration: 1.5, ease: 'power2.inOut' }, startDelay + 3);
        if (fast) {
            const start = 0.2;

            tl.set(bendDirDeg, { value: 0 }, start);
            tl.set(totalSpeedStr, { value: 1 }, start);
            tl.set(bendAngleDeg, { value: 0 }, start);
            tl.fromTo(uInProgress, { value: 0 }, { value: 1, duration: 0.5, ease: 'none' }, start);

            const out = 0.6;
            tl.to(totalSpeedStr, { value: 0.2, duration: 0.5, ease: 'sine.inOut' }, out);
            tl.to(uInProgress, { value: 0, duration: 0.5, ease: 'sine.out' }, out + 0.4);
            tl.add(() => emit('complete'), 0); // out + 0.3
        } else {
            tl.set(totalSpeedStr, { value: 1 });
            tl.fromTo(
                bendDirDeg,
                { value: 0 },
                { value: 360, duration: 6, ease: 'none', repeat: 1 },
                0
            );
            tl.fromTo(
                uInProgress,
                { value: 0 },
                { value: 1, duration: 3, ease: 'none' },
                GRAIL_TUNNEL_SET
            );
            tl.fromTo(
                bendAngleDeg,
                { value: 0.01 },
                { value: 30, duration: 3, ease: 'sine.inOut' },
                GRAIL_TUNNEL_REVEAL + 0.525
            );
            tl.to(
                bendAngleDeg,
                { value: 0.01, duration: 1, ease: 'sine.inOut' },
                GRAIL_TUNNEL_WIPE + 0.225
            );
            tl.to(
                totalSpeedStr,
                { value: 0.2, duration: 1, ease: 'sine.inOut' },
                GRAIL_TUNNEL_WIPE + 0.225
            );
            tl.to(
                uInProgress,
                { value: 0, duration: 1, ease: 'sine.out' },
                GRAIL_TUNNEL_WIPE + 0.6
            );
            tl.add(() => emit('complete'), GRAIL_TUNNEL_WIPE + 0.8);
        }

        return tl;
    };

    const hide = () => {
        active.value = false;
    };

    useGlobalAssets(onLoaded);

    const updateGeometry = () => {
        geo.dispose();
        // geo = new ConeGeometry(radiusTop.value, height.value, segments.value);
        geo = new CylinderGeometry(
            radiusTop.value,
            radiusBottom.value,
            height.value,
            segments.value,
            heightSegments.value,
            openEnded.value
        );
        tunnel.geometry = geo;
    };

    usePane(
        [
            { name: 'active', value: active },
            { name: 'uInProgress', value: uInProgress, options: basePaneOptions },

            {
                name: 'bendAngleDeg',
                value: bendAngleDeg,
                options: { min: 0.1, max: 180, step: 0.1 },
            },
            // radius is derived from height & angle; height already exposed above
            { name: 'bendDirDeg', value: bendDirDeg, options: { min: -180, max: 180, step: 0.1 } },

            { name: 'uNoiseScale1', value: uNoiseScale1, options: { min: 0, max: 10, step: 0.01 } },
            { name: 'uNoiseScale2', value: uNoiseScale2, options: { min: 0, max: 10, step: 0.01 } },
            { name: 'uNoiseScale3', value: uNoiseScale3, options: { min: 0, max: 10, step: 0.01 } },
            { name: 'uNoiseSpeed', value: uNoiseSpeed, options: { min: 0, max: 0.2, step: 0.001 } },
            {
                name: 'uNoiseSpeed3',
                value: uNoiseSpeed3,
                options: { min: 0, max: 0.3, step: 0.001 },
            },

            {
                name: 'uColorBrightness',
                value: uColorBrightness,
                options: { min: 0, max: 10, step: 0.01 },
            },

            { name: 'color1', value: color1 },
            { name: 'color2', value: color2 },
            { name: 'color3', value: color3 },

            { name: 'radiusTop', value: radiusTop, options: { min: 0, max: 10, step: 0.01 } },
            { name: 'radiusBottom', value: radiusBottom, options: { min: 0, max: 10, step: 0.01 } },
            { name: 'height', value: height, options: { min: 0, max: 100, step: 0.01 } },
            { name: 'segments', value: segments, options: { min: 1, max: 50, step: 1 } },
            {
                name: 'heightSegments',
                value: heightSegments,
                options: { min: 1, max: 50, step: 1 },
            },
            { name: 'openEnded', value: openEnded, options: { min: 0, max: 1, step: 0.01 } },
            { name: 'rx', value: rx, options: { min: 0, max: 180, step: 0.01 } },
        ],
        { title: 'Grail Tunnel', expanded: true }
    );

    watchEffect(() => {
        tunnel.rotation.x = toRadians(rx.value);
    });

    watchEffect(() => {
        material.uInProgress = uInProgress.value;
    });

    watchEffect(() => {
        material.uNoiseScale1 = uNoiseScale1.value;
        material.uNoiseScale2 = uNoiseScale2.value;
        material.uNoiseScale3 = uNoiseScale3.value;
        material.uNoiseSpeed = uNoiseSpeed.value;
        material.uNoiseSpeed3 = uNoiseSpeed3.value;

        // material.uColors[0].set(color1.value);
        // material.uColors[1].set(color2.value);
        // material.uColors[2].set(color3.value);

        material.uColor1.set(slabColor1.value);
        material.uColor2.set(slabColor2.value);
        material.uColorBrightness = uColorBrightness.value;

        // Bend uniforms
        material.uBendAngle = toRadians(bendAngleDeg.value);
        material.uBendHeight = height.value;
        const dirRad = toRadians(bendDirDeg.value);
        material.uBendDirection.set(Math.cos(dirRad), Math.sin(dirRad));
    });

    watchEffect(updateGeometry);

    useRafBool(active, ({ delta }) => {
        material.uTime += delta * 0.001 * totalSpeedStr.value;
    });

    const emit = defineEmits(['start', 'complete']);

    defineExpose({ show, hide });
</script>
